/** Decimals of a currency's minor unit (2 for USD, 0 for JPY, 3 for KWD). */
export function currencyExponent(currency: string): number {
  try {
    return (
      new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

/** Shows an integer amount of minor units, e.g. 123456 USD → "1,234.56 USD". */
export function formatMinor(minor: number, currency: string): string {
  const exponent = currencyExponent(currency);
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(minor);
  const whole = Math.floor(abs / 10 ** exponent);
  const fraction = exponent === 0 ? '' : `.${String(abs % 10 ** exponent).padStart(exponent, '0')}`;
  return `${sign}${whole.toLocaleString('en-US')}${fraction} ${currency}`;
}

/**
 * Turns what a person typed ("1234.5") into integer minor units (123450) without floating point.
 * Returns null for anything that is not a plain non-negative decimal with at most the currency's decimals.
 */
export function parseMajor(text: string, currency: string): number | null {
  const exponent = currencyExponent(currency);
  const match = /^(\d{1,12})(?:\.(\d+))?$/.exec(text.trim());
  if (!match) return null;
  const fraction = match[2] ?? '';
  if (fraction.length > exponent) return null;
  const minor = Number(match[1]) * 10 ** exponent + Number(fraction.padEnd(exponent, '0') || '0');
  return Number.isSafeInteger(minor) ? minor : null;
}
